import {
  Bill,
  Invoice,
  InvoiceMode,
  InvoiceOrderItem,
  InvoiceStatus,
  Lot,
  LotConsumption,
  Order,
  OrderAction,
  OrderItem,
  OrderStatus,
  Product,
  ProductPackaging,
  PurchaseOrderStatus,
  Warehouse,
} from "@prisma/client";

import { reverseSubmitBillBlockMessage } from "~/modules/accounting/bill-linkage-ui";
import { isDirectShip } from "~/modules/orders/order-type";
import { shouldBlockDemoteForActiveBillLinkage } from "~/shared/accounting/bill-document-eligibility";
import { orderInvoiceReadinessBlocker } from "~/shared/orders/order-invoice-readiness";
import { enumTermToText, transforms } from "~/utils/strings";
import { valueNotNull } from "~/utils/types/utility";

export { reverseSubmitBillBlockMessage };

export type ActionOrder = Pick<
  Order,
  "id" | "orderNo" | "status" | "customerId" | "warehouseId" | "type" | "isReadOnly" | "shippedAt" | "deliveredAt"
> & {
  invoices: Pick<Invoice, "id" | "status" | "invoiceNo" | "dueDate" | "amountDue" | "submittedDate" | "postedAt">[];
  items: (Pick<OrderItem, "id" | "productId" | "lotId" | "invoiceMode" | "quantity" | "calculatedQuantity"> & {
    invoiceOrderItems?: Array<
      | { invoiceItem: { invoice: Pick<Invoice, "status"> } }
      | Pick<InvoiceOrderItem, "id" | "orgId" | "packagePrice" | "orderItemId" | "invoiceItemId" | "qtyInvoiced">
    >;
    invoiceItems?: Array<{ invoice: Pick<Invoice, "status"> }>;
    // These are optional - only needed for ReverseSubmit modal to show lot deallocation details
    lot?: (Pick<Lot, "id" | "warehouseId" | "lotNumber"> & { warehouse?: Pick<Warehouse, "name"> | null }) | null;
    // For DSO orders, lotConsumption tracks lot allocation when lotId is null (system-reserved lots)
    lotConsumption?: Pick<LotConsumption, "id"> | null;
    product?:
      | (Pick<Product, "code" | "packagingType"> & {
          packaging?: Pick<ProductPackaging, "unitOfMeasure"> | null;
        })
      | null;
  })[];
  warehouse: Pick<Warehouse, "name"> | null;
  purchaseOrderId?: string | null;
  /** Non-void bills on the linked PO. Only populated for DSO orders; empty array for non-DSO. */
  bills?: Pick<Bill, "id" | "status" | "billNo">[];
  /** Non-void bills linked via order costs (freight/fees on the SO). */
  orderCostBills?: Pick<Bill, "id" | "status" | "billNo">[];
};

export type OrderActionObject =
  | {
      action: OrderAction;
      allowed: true;
    }
  | {
      action: OrderAction;
      allowed: false;
      message: string;
    };

type OrderActionCheckResult = null | OrderActionObject;
type OrderActionCheck = (order: ActionOrder) => OrderActionCheckResult;
type OrderStatusAction = OrderActionObject | OrderActionCheck;

const termToText = new Map<OrderAction, string>();
Object.keys(OrderAction).forEach(v =>
  termToText.set(
    v as OrderAction,
    enumTermToText(
      v,
      transforms("Submit Invoice", "Create Invoice")
        .add("Create Packing Slip", "Packing Slip")
        .add("Create Warehouse Release", "Warehouse Release")
        .add("Create Pick Up Request", "Pick Up Request")
        .add("Create Bill Of Lading", "Bill of Lading")
        .add("Create Sales Order Confirmation", "Sales Order Confirmation")
        .add("Create Pick List", "Pick List"),
    ),
  ),
);

function checkLots(order: ActionOrder, action: OrderAction): OrderActionCheckResult {
  const isDso = isDirectShip(order.type);

  return order.items.every(i => {
    // Non-product items (e.g., fees, costs) don't require lot assignment
    if (i.productId === null) {
      return true;
    }
    // Item has a direct lot assignment
    if (i.lotId !== null) {
      return true;
    }
    // For non-DSO orders, lotId is required
    if (!isDso) {
      return false;
    }
    // DSO: allow null lotId only when LotConsumption exists (system-reserved lots like UNSPECIFIED)
    return i.lotConsumption != null;
  })
    ? { action, allowed: true }
    : null;
}
export const checkLotsForShip: OrderActionCheck = (o: ActionOrder) => checkLots(o, OrderAction.SHIP);

export const checkForDirectShip: OrderActionCheck = (order: ActionOrder) =>
  isDirectShip(order.type) ? { action: OrderAction.SHIP, allowed: true } : null;

/** Safely access bills on the order — empty array when not populated (non-DSO orders). */
const getBills = (order: ActionOrder) => order.bills ?? [];

function orderCostBillBlocksDemote(order: ActionOrder, targetStatus: OrderStatus): boolean {
  return shouldBlockDemoteForActiveBillLinkage("ORDER", targetStatus) && (order.orderCostBills ?? []).length > 0;
}

function linkedPoBillBlocksDemote(order: ActionOrder, poTargetStatus: PurchaseOrderStatus): boolean {
  return isDirectShip(order.type) && shouldBlockDemoteForActiveBillLinkage("PURCHASE_ORDER", poTargetStatus) && getBills(order).length > 0;
}

function billsBlockDemoteToOrderStatus(order: ActionOrder, targetStatus: OrderStatus, poTargetStatus: PurchaseOrderStatus): boolean {
  return orderCostBillBlocksDemote(order, targetStatus) || linkedPoBillBlocksDemote(order, poTargetStatus);
}

export { billsBlockDemoteToOrderStatus };

function checkForWarehouse(order: ActionOrder, action: OrderAction): OrderActionCheckResult {
  const itemWarehouses = order.items
    .filter(valueNotNull("lot"))
    .flatMap(i => i.lot)
    .filter(valueNotNull("warehouseId"))
    .reduce<Record<string, string[]>>((acc, item) => {
      const entries = acc[item.warehouseId] ?? [];
      entries.push(item.id);
      acc[item.warehouseId] = entries;
      return acc;
    }, {});
  const distinctItemWarehouses = Object.keys(itemWarehouses).length;

  if (order.warehouseId != null || distinctItemWarehouses === 1) {
    return { action, allowed: true };
  }
  return {
    action,
    allowed: false,
    message: distinctItemWarehouses > 1 ? "Too many Warehouses" : "No Warehouse Found",
  };
}
const checkLotsForCreatePickUpRequest: OrderActionCheck = (o: ActionOrder) => checkForWarehouse(o, OrderAction.CREATE_PICK_UP_REQUEST);
const checkLotsForCreatePackingSlip: OrderActionCheck = (o: ActionOrder) => checkForWarehouse(o, OrderAction.CREATE_PACKING_SLIP);
const checkLotsForCreateBillOfLading: OrderActionCheck = (o: ActionOrder) => checkForWarehouse(o, OrderAction.CREATE_BILL_OF_LADING);
const checkLotsForCreateWarehouseRelease: OrderActionCheck = (o: ActionOrder) => checkForWarehouse(o, OrderAction.CREATE_WAREHOUSE_RELEASE);
const checkLotsForCreatePickList: OrderActionCheck = (o: ActionOrder) => checkForWarehouse(o, OrderAction.CREATE_PICK_LIST);

/**
 * Evaluate if Order can be "reverse submitted". In order for an Order to be reversible
 * it must meet the following requirements:
 * - Does not have pending Invoices
 * - Does not have non-void bills on linked PO (DSO orders only)
 * - Does not have non-void bills linked to order costs
 *
 * Note: Lots are auto-deallocated when reversing to DRAFT - no manual removal required.
 */
export const checkForReverseSubmit: OrderActionCheck = (order: ActionOrder) => {
  const hasActiveInvoices = order.invoices.some(i => i.status !== InvoiceStatus.VOID);
  if (hasActiveInvoices) {
    return null;
  }
  if (billsBlockDemoteToOrderStatus(order, OrderStatus.DRAFT, PurchaseOrderStatus.DRAFT)) {
    return { action: OrderAction.REVERSE_SUBMIT, allowed: false, message: reverseSubmitBillBlockMessage };
  }
  return { action: OrderAction.REVERSE_SUBMIT, allowed: true };
};

/**
 * Evaluate if Order can submit an invoice. In order for an Order to submit
 * an invoice it must mee the following requirements:
 * - Order does not have Invoices
 * - Order does not have Consignment items
 * @NOTE this is effectively the same requirements as creating a Proforma invoice
 */
export const checkForConvertToInvoice: OrderActionCheck = (order: ActionOrder) => {
  const hasInvoices = !!order.invoices.length;
  const hasConsignmentItems = order.items.some(item => item.invoiceMode === InvoiceMode.CONSIGNMENT);

  if (hasInvoices || hasConsignmentItems || orderInvoiceReadinessBlocker(order)) {
    return null;
  }
  return { action: OrderAction.SUBMIT_INVOICE, allowed: true };
};

/**
 * Check if an invoice has been posted to the GL (has financial impact).
 * DRAFT invoices have no GL entries and don't block workflow operations.
 * POSTED/PARTIALLY_PAID/PAID invoices have recognized revenue/COGS and block reverse ship.
 */
const isInvoicePosted = (invoice: Pick<Invoice, "status">): boolean => {
  const postedStatuses: InvoiceStatus[] = [InvoiceStatus.POSTED, InvoiceStatus.PARTIALLY_PAID, InvoiceStatus.PAID];
  return postedStatuses.includes(invoice.status);
};

/**
 * Evaluate if Order can reverse shipment.
 * Cannot reverse ship while any posted invoice exists (POSTED/PARTIALLY_PAID/PAID).
 * Linked PO / order-cost bills do not block — SHIPPED and CONFIRMED are both billable (SC-2089).
 */
export const checkForReverseShip: OrderActionCheck = (order: ActionOrder) => {
  const postedInvoices = order.invoices.filter(isInvoicePosted);
  if (postedInvoices.length > 0) {
    return {
      action: OrderAction.REVERSE_SHIPPED,
      allowed: false,
      message: "Void invoice first",
    };
  }
  return { action: OrderAction.REVERSE_SHIPPED, allowed: true };
};

/**
 * Evaluate if Order can submit a Proforma invoice. In order for an Order to submit
 * a Proforma invoice it must meet the following requirements:
 * - Order does not have Invoices
 * - Order does not have Consignment items
 * @NOTE this is effectively the same requirements as creating an invoice
 */
export const checkForProformaInvoice: OrderActionCheck = (order: ActionOrder) => {
  const hasInvoices = !!order.invoices.length;
  const hasConsignmentItems = order.items.some(item => item.invoiceMode === InvoiceMode.CONSIGNMENT);
  if (hasInvoices || hasConsignmentItems) {
    return null;
  }
  return { action: OrderAction.CREATE_PROFORMA_INVOICE, allowed: true };
};

const addPaymentInvoiceStatuses: InvoiceStatus[] = [InvoiceStatus.POSTED, InvoiceStatus.PARTIALLY_PAID];
/**
 * Evaluate if Order can create a Payment. In order for an Order to create
 * a payment it must meet the following requirements:
 * - Order does not have invoices that have statuses:
 *   - `POSTED`
 *   - `PARTIALLY_PAID`
 * @NOTE see `addPaymentInvoiceStatuses` for list of statuses evaluated against
 */
export const checkForAddPayment: OrderActionCheck = (order: ActionOrder) => {
  const statuses: InvoiceStatus[] = order.invoices.flatMap(i => i.status);
  const paymentStatuses = statuses.filter(s => addPaymentInvoiceStatuses.includes(s));
  const hasInvalidPaymentStatuses = !!paymentStatuses.length;
  if (hasInvalidPaymentStatuses) {
    return null;
  }
  return { action: OrderAction.ADD_PAYMENT, allowed: true };
};

/**
 * Evaluate if Order can be marked "complete". In order for an Order to be completed
 * it must meet the following requirements:
 * - Order has at least one non-void Invoice
 * - All non-void Invoices are PAID
 */
export const checkForMarkCompleted: OrderActionCheck = (order: ActionOrder) => {
  const nonVoidInvoices = order.invoices.filter(i => i.status !== InvoiceStatus.VOID);
  const paidInvoices = nonVoidInvoices.filter(i => i.status === InvoiceStatus.PAID);
  const hasNonVoidInvoices = nonVoidInvoices.length > 0;
  const allNonVoidInvoicesPaid = paidInvoices.length === nonVoidInvoices.length;
  if (!hasNonVoidInvoices || !allNonVoidInvoicesPaid) {
    return null;
  }
  return { action: OrderAction.MARK_COMPLETED, allowed: true };
};

/** Invoice statuses that carry an applied customer payment. */
export const paidOrPartiallyPaidInvoiceStatuses: InvoiceStatus[] = [InvoiceStatus.PARTIALLY_PAID, InvoiceStatus.PAID];

/**
 * Evaluate if a COMPLETED Order can be reverted to DELIVERED (SC-1648).
 *
 * COMPLETED is granted by `checkForMarkCompleted` once every non-void Invoice is PAID, so the
 * demote is offered once no Invoice carries an applied payment any more — which is the state an
 * order lands in after its AR payment is reversed and voided.
 *
 * A POSTED unpaid Invoice does not block: DELIVERED holds one in the ordinary pre-payment state.
 * The payment part of this gate is `errorOnPaidInvoices` (the gate for voiding an Order) widened to
 * the line-linked and consignment Invoice relations, plus the read-only, delivery-date and
 * direct-invoice conditions below.
 */
export const checkForReverseMarkCompleted: OrderActionCheck = (order: ActionOrder) => {
  // Read-only orders (QBO cutover baselines) are never mutated.
  if (order.isReadOnly) {
    return null;
  }
  // The demote lands in DELIVERED, so the order has to have been delivered. The consignment
  // completion path (`invoice.ts` invoiceSubmit) can set COMPLETED on an order that never was, and
  // the service refuses those — without this the dropdown would offer an option that always throws.
  if (order.deliveredAt == null) {
    return null;
  }
  // All three linkages, as in `checkForUpdatePrice`: an Invoice reaches an Order through canonical
  // membership, an InvoiceItem on an order item, or the consignment InvoiceOrderItem join. Checking
  // membership alone would still let a COMPLETED consignment order demote while its invoice is PAID.
  // COMPLETED has two grantors. The direct-invoice paths grant it on "every non-void invoice PAID",
  // which this action reverses. The consignment path grants it on "all order items fully invoiced"
  // (`invoice.ts` invoiceSubmit), a rule payment state says nothing about — and `checkForMarkCompleted`
  // reads only direct invoices, so a demoted consignment order could never be completed again.
  // Require a non-void direct invoice, so the action only reverses completions it can also restore.
  const hasNonVoidDirectInvoice = order.invoices.some(invoice => invoice.status !== InvoiceStatus.VOID);
  if (!hasNonVoidDirectInvoice) {
    return null;
  }
  const hasAppliedDirectPayment = order.invoices.some(invoice => paidOrPartiallyPaidInvoiceStatuses.includes(invoice.status));
  const hasAppliedLineLinkedPayment = order.items.some(item =>
    (item.invoiceItems ?? []).some(invoiceItem => paidOrPartiallyPaidInvoiceStatuses.includes(invoiceItem.invoice.status)),
  );
  const hasAppliedConsignmentPayment = order.items.some(item =>
    (item.invoiceOrderItems ?? []).some(invoiceOrderItem => {
      if (!("invoiceItem" in invoiceOrderItem)) {
        return false;
      }
      return paidOrPartiallyPaidInvoiceStatuses.includes(invoiceOrderItem.invoiceItem.invoice.status);
    }),
  );
  if (hasAppliedDirectPayment || hasAppliedLineLinkedPayment || hasAppliedConsignmentPayment) {
    return null;
  }
  return { action: OrderAction.REVERSE_MARK_COMPLETED, allowed: true };
};

const voidInvoiceStatuses: InvoiceStatus[] = [InvoiceStatus.DRAFT, InvoiceStatus.POSTED, InvoiceStatus.VOID];
/**
 * Evalute if Order can void an Invoice. In order for an Order to void an
 * Invoice it must meet the following requirements:
 * - Order has Invoices
 * - Order has all Invoice in valid status (all invoices have same status in `voidInvoiceStatuses`)
 */
export const checkForVoidInvoice: OrderActionCheck = (order: ActionOrder) => {
  const statuses: InvoiceStatus[] = order.invoices.flatMap(i => i.status);
  const allowableStatuses = statuses.filter(s => voidInvoiceStatuses.includes(s));
  const hasInvoices = !!order.invoices.length;
  const hasAllInvoicesInValidStatus = statuses.length === allowableStatuses.length;
  if (!hasInvoices || !hasAllInvoicesInValidStatus) {
    return null;
  }
  return { action: OrderAction.VOID_INVOICE, allowed: true };
};

export const checkForUpdatePrice: OrderActionCheck = (order: ActionOrder) => {
  const hasActiveDirectInvoice = order.invoices.some(invoice => invoice.status !== InvoiceStatus.VOID);
  const hasActiveLineLinkedInvoice = order.items.some(item =>
    (item.invoiceItems ?? []).some(invoiceItem => invoiceItem.invoice.status !== InvoiceStatus.VOID),
  );
  const hasActiveConsignmentInvoice = order.items.some(item =>
    (item.invoiceOrderItems ?? []).some(invoiceOrderItem => {
      if (!("invoiceItem" in invoiceOrderItem)) {
        return false;
      }
      return invoiceOrderItem.invoiceItem.invoice.status !== InvoiceStatus.VOID;
    }),
  );
  if (order.isReadOnly || hasActiveDirectInvoice || hasActiveLineLinkedInvoice || hasActiveConsignmentInvoice) {
    return null;
  }
  return { action: OrderAction.UPDATE_PRICE, allowed: true };
};

/**
 * Evaluate if an Order can be voided. In order for an Order to be voided
 * it must meet the following requirements:
 * - Order cannot have Invoices
 * - Order must not be able to void an Invoice (see `checkForVoidInvoice`)
 * - Does not have non-void bills on linked PO (DSO) or order costs — VOID is non-billable.
 */
export const checkForVoidOrder: OrderActionCheck = (order: ActionOrder) => {
  const hasInvoices = !!order.invoices.length;
  const canVoidInvoice = checkForVoidInvoice(order);
  if ((hasInvoices && canVoidInvoice) || billsBlockDemoteToOrderStatus(order, OrderStatus.VOID, PurchaseOrderStatus.VOID)) {
    return null;
  }
  return { action: OrderAction.MARK_VOID, allowed: true };
};

/**
 * Evaluate if an Order can edit lots. In order for an Order to edit
 * its lots it must meet the following requirements:
 * - Order cannot have invoices
 * - Order cannot be Direct Ship type (see `isDirectShip`)
 */
export const checkForEditLots: OrderActionCheck = (order: ActionOrder) => {
  const hasInvoices = !!order.invoices.length;
  const orderIsDirectShip = isDirectShip(order.type);
  if (hasInvoices || orderIsDirectShip) {
    return null;
  }
  return { action: OrderAction.EDIT_LOTS, allowed: true };
};

/**
 * Evaluate if an Order can be converted to a Direct Ship type. In order
 * for an Order to be convertable to Direct Ship it must meet the
 * following requirements:
 * - Order cannot have Invoices
 * - Order cannot already be a Direct Ship type (see `isDirectShip`)
 * - Order cannot be in DELIVERED or COMPLETED status (inventory state is finalized)
 */
export const checkForConvertToDirectShip: OrderActionCheck = (order: ActionOrder) => {
  const hasInvoices = !!order.invoices.length;
  const orderIsDirectShip = isDirectShip(order.type);
  const isDeliveredOrCompleted = order.status === OrderStatus.DELIVERED || order.status === OrderStatus.COMPLETED;

  if (hasInvoices || orderIsDirectShip) {
    return null;
  }

  // Block conversion if order is delivered or completed - inventory state is finalized
  if (isDeliveredOrCompleted) {
    return {
      action: OrderAction.CONVERT_TO_DIRECT_SHIP,
      allowed: false,
      message: "Cannot convert delivered/completed orders. Contact support if you need to fix inventory.",
    };
  }

  return { action: OrderAction.CONVERT_TO_DIRECT_SHIP, allowed: true };
};

/**
 * Evaluate if an Order can be converted to a Stock Order type. In order
 * for an Order to be convertable to Stock Order it must meet the
 * following requirements:
 * - Order cannot have Invoices
 * - Order is not Direct Ship type (see `isDirectShip`)
 * - Order cannot be in DELIVERED or COMPLETED status (inventory state is finalized)
 */
export const checkForConvertToStockOrder: OrderActionCheck = (order: ActionOrder) => {
  const hasInvoices = !!order.invoices.length;
  const orderIsDirectShip = isDirectShip(order.type);
  const isDeliveredOrCompleted = order.status === OrderStatus.DELIVERED || order.status === OrderStatus.COMPLETED;

  if (hasInvoices || !orderIsDirectShip) {
    return null;
  }

  // Block conversion if order is delivered or completed - inventory state is finalized
  if (isDeliveredOrCompleted) {
    return {
      action: OrderAction.CONVERT_TO_STOCK_ORDER,
      allowed: false,
      message: "Cannot convert delivered/completed orders. Contact support if you need to fix inventory.",
    };
  }

  return { action: OrderAction.CONVERT_TO_STOCK_ORDER, allowed: true };
};

const voidOrderActions: OrderStatusAction[] = [
  { action: OrderAction.CREATE_SALES_ORDER_CONFIRMATION, allowed: true },
  { action: OrderAction.COPY, allowed: true },
  { action: OrderAction.SEND_EMAIL, allowed: true },
];

const draftOrderActions: OrderStatusAction[] = [
  { action: OrderAction.CREATE_SALES_ORDER_CONFIRMATION, allowed: true },
  { action: OrderAction.MODIFY, allowed: true },
  { action: OrderAction.SUBMIT, allowed: true },
  { action: OrderAction.MARK_VOID, allowed: true },
  { action: OrderAction.COPY, allowed: true },
  { action: OrderAction.MODIFY_ITEMS, allowed: true },
  { action: OrderAction.SEND_EMAIL, allowed: true },
  checkForConvertToDirectShip,
  checkForConvertToStockOrder,
  checkLotsForCreatePickUpRequest,
  checkLotsForCreatePackingSlip,
  checkLotsForCreateBillOfLading,
  checkLotsForCreateWarehouseRelease,
  checkLotsForCreatePickList,
];

const confirmedOrderActions: OrderStatusAction[] = [
  { action: OrderAction.CREATE_SALES_ORDER_CONFIRMATION, allowed: true },
  { action: OrderAction.MODIFY, allowed: true },
  { action: OrderAction.COPY, allowed: true },
  { action: OrderAction.MODIFY_ITEMS, allowed: true },
  checkForUpdatePrice,
  { action: OrderAction.SEND_EMAIL, allowed: true },
  checkForConvertToDirectShip,
  checkForConvertToStockOrder,
  checkLotsForCreatePickUpRequest,
  checkLotsForCreatePackingSlip,
  checkLotsForCreateBillOfLading,
  checkLotsForCreateWarehouseRelease,
  checkLotsForCreatePickList,
  checkForEditLots,
  checkLotsForShip,
  checkForDirectShip,
  checkForVoidOrder,
  checkForConvertToInvoice,
  checkForProformaInvoice,
  checkForAddPayment,
  checkForVoidInvoice,
  checkForReverseSubmit,
];

const shippedOrderActions: OrderStatusAction[] = [
  { action: OrderAction.CREATE_SALES_ORDER_CONFIRMATION, allowed: true },
  { action: OrderAction.MODIFY, allowed: true },
  { action: OrderAction.MARK_DELIVERED, allowed: true },
  { action: OrderAction.COPY, allowed: true },
  checkForUpdatePrice,
  checkForReverseShip,
  { action: OrderAction.SEND_EMAIL, allowed: true },
  checkForEditLots,
  checkForConvertToDirectShip,
  checkForConvertToStockOrder,
  checkLotsForCreatePickUpRequest,
  checkLotsForCreatePackingSlip,
  checkLotsForCreateBillOfLading,
  checkLotsForCreateWarehouseRelease,
  checkLotsForCreatePickList,
  checkForConvertToInvoice,
  checkForAddPayment,
  checkForVoidInvoice,
];

const deliveredOrderActions: OrderStatusAction[] = [
  { action: OrderAction.CREATE_SALES_ORDER_CONFIRMATION, allowed: true },
  { action: OrderAction.MODIFY, allowed: true },
  { action: OrderAction.COPY, allowed: true },
  { action: OrderAction.REVERSE_MARK_DELIVERED, allowed: true },
  { action: OrderAction.SEND_EMAIL, allowed: true },
  checkLotsForCreatePickUpRequest,
  checkLotsForCreatePackingSlip,
  checkLotsForCreateBillOfLading,
  checkLotsForCreateWarehouseRelease,
  checkLotsForCreatePickList,
  checkForMarkCompleted,
  checkForConvertToInvoice,
  checkForAddPayment,
  checkForVoidInvoice,
];

const completedOrderActions: OrderStatusAction[] = [
  { action: OrderAction.CREATE_SALES_ORDER_CONFIRMATION, allowed: true },
  { action: OrderAction.COPY, allowed: true },
  { action: OrderAction.SEND_EMAIL, allowed: true },
  checkForReverseMarkCompleted,
];

const orderStatusActions: Record<OrderStatus, OrderStatusAction[]> = {
  [OrderStatus.VOID]: voidOrderActions,
  [OrderStatus.DRAFT]: draftOrderActions,
  [OrderStatus.CONFIRMED]: confirmedOrderActions,
  [OrderStatus.SHIPPED]: shippedOrderActions,
  [OrderStatus.DELIVERED]: deliveredOrderActions,
  [OrderStatus.COMPLETED]: completedOrderActions,
};

export function orderActionToText(action: OrderAction) {
  return termToText.has(action) ? termToText.get(action) : `Invalid: ${action}`;
}

export function getAvailableOrderActions(order: ActionOrder) {
  return orderStatusActions[order.status].map(x => (typeof x === "function" ? x(order) : x)).filter(x => x !== null) as OrderActionObject[];
}

export function getAvailableAndAllowedOrderActions(order: ActionOrder) {
  return getAvailableOrderActions(order).filter(a => a.allowed);
}

export function isActionAvailableAndAllowed(order: ActionOrder, action: OrderAction) {
  return getAvailableAndAllowedOrderActions(order)
    .map(a => a.action)
    .includes(action);
}

/**
 * Checks if order is not in a dead-end status (COMPLETED or VOID) and has no invoices.
 * Used for item/header manipulation which is blocked once invoiced or finalized.
 */
function notDeadEndStatusAndNoInvoices(order: ActionOrder) {
  return order.status !== OrderStatus.COMPLETED && order.status !== OrderStatus.VOID && order.invoices.length === 0;
}

/** Checks if the order is marked as read-only */
const orderIsReadOnly = (order: ActionOrder) => order.isReadOnly;

/**
 * Checks if the order header (customer, dates, etc.) can be modified.
 * Blocked for COMPLETED/VOID status, if invoiced, or if read-only.
 */
export function canManipulateHeader(order: ActionOrder) {
  return !orderIsReadOnly(order) && notDeadEndStatusAndNoInvoices(order);
}

/**
 * Checks if lot assignments can be edited on product items.
 * Uses the EDIT_LOTS action permission.
 */
export function canManipulateProductItemsLot(order: ActionOrder) {
  return !orderIsReadOnly(order) && isActionAvailableAndAllowed(order, OrderAction.EDIT_LOTS);
}

/**
 * Checks if product line items can be added/removed/edited.
 * Blocked for COMPLETED/VOID status, if invoiced, or if read-only.
 */
export function canManipulateProductItems(order: ActionOrder) {
  return !orderIsReadOnly(order) && notDeadEndStatusAndNoInvoices(order) && isActionAvailableAndAllowed(order, OrderAction.MODIFY_ITEMS);
}

/**
 * Checks if non-product items (fees, adjustments) can be edited.
 * Blocked for COMPLETED/VOID status, if invoiced, or if read-only.
 */
export function canManipulateNonProductItems(order: ActionOrder) {
  return !orderIsReadOnly(order) && notDeadEndStatusAndNoInvoices(order);
}

/**
 * Checks if costs can be manipulated on an order.
 * Costs CAN be edited even after invoicing and after COMPLETED status (they only affect margin/KPI, not customer invoices).
 * COMPLETED is allowed because supplier bills (freight, handling fees, etc.)
 * often arrive after the order is delivered/completed.
 * Blocked only for VOID status or if order is read-only.
 */
export function canManipulateCosts(order: ActionOrder) {
  return !orderIsReadOnly(order) && order.status !== OrderStatus.VOID;
}

const CONFIRMED_READONLY_FIELDS: string[] = ["invoiceMode", "customerId", "orderedAt", "type"];

const DELIVERED_READONLY_FIELDS: string[] = [
  // assignedToId stays editable after delivery. Header save covers it while the header
  // is still open; updateAssignedTo covers shipped and invoiced orders whose header is locked.
  "customerOrderNo",
  "freightMethod",
  "freightTerms",
  "incoterm",
  "orderedById",
  "requiredBy",
  "salesPersonId",
  "shipBy",
  "shipToAddressId",
  "shippingCarrier",
  "carrierContactId",
  "shippingFob",
  "shippingRemarks",
  "warehouseId",
  "supplierTermsOfPaymentId",
  "shipFromLocationId",
];

export function getReadOnlyHeaderFields(status: OrderStatus) {
  switch (status) {
    case OrderStatus.CONFIRMED:
    case OrderStatus.SHIPPED:
      return [...CONFIRMED_READONLY_FIELDS];
    case OrderStatus.DELIVERED:
      return [...CONFIRMED_READONLY_FIELDS, ...DELIVERED_READONLY_FIELDS];
    default:
      return ["type", "invoiceMode"];
  }
}
